import React, { Fragment } from "react"

import {
    Code,
    combinePatterns,
    patternParts,
} from "../rendered-listing-pattern"

const help = { marginTop: "5px", marginBottom: "0px" }
const valid = { ...help, color: "green" }
const invalid = { ...help, color: "red" }
// For settings that are valid but probably not what's meant, in amber like the dashboard's other warnings: red ✗
// is for settings that won't work
const caution = { ...help, padding: "6px 10px" }
const MAX_PAGES_SHOWN = 8

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`

const linkList = {
    ...help,
    marginLeft: "20px",
    paddingLeft: "20px",
    wordBreak: "break-all",
}

// A link found on the page, opened in a new tab so the form keeps its state
const LinkOut = ({ href }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">
        {href}
    </a>
)

// dashboard-api lists a group's links; one from before it did has only examples
const linksOf = (group) => group.links || group.examples

// A link without its query, fragment or trailing slash, as dashboard-api offers pages
const pagePath = (link) => link.split("#")[0].split("?")[0].replace(/\/+$/, "")

// The group whose listing pages a group's links are, if any: WEF's topic pages (weforum.org/stories/*) are the
// pages one level up from its stories (weforum.org/stories/*/*), so they list stories rather than being content.
// The listed group needs a few links of its own, so one stray link, such as a template's, doesn't mark its parent.
const listedBy = (group, groups) => {
    const own = new Set(linksOf(group).map(pagePath))
    return groups.find(
        (other) =>
            other !== group &&
            other.count >= 3 &&
            other.pages.filter((page) => own.has(pagePath(page))).length * 2 >=
                own.size
    )
}

// A Rendered listing source's settings, set up by looking at the source URL: dashboard-api renders it
// (rendered_listing_discover) and groups its links by shape, the moderator ticks the groups that are content, and
// the pages one level up are offered as more pages to check. The pattern and pages stay editable under Advanced.
// dashboard-api checks them as they change (`check`, from rendered_listing_check) by the rules the save and the
// scraper apply.
const SourceEditRenderedListingDetails = ({
    url,
    linkPattern,
    listingUrls,
    check,
    isChecking,
    checkFailed,
    testLink,
    discover,
    isDiscovering,
    discoverError,
    onEventChange,
    onValueChange,
    onTestLinkChange,
    onDiscover,
}) => {
    const sourceUrl = (url || "").trim()
    const pages = (listingUrls || []).map((page) => page.trim())
    const parts = patternParts(linkPattern)
    const groups = (discover && discover.groups) || []
    const ticked = groups.filter((group) => parts.includes(group.pattern))
    // Ticked groups that are listing pages for another group, such as WEF's topic pages
    const misTicked = ticked
        .map((group) => ({ group, lists: listedBy(group, groups) }))
        .filter(({ lists }) => lists)
    const offered = [...new Set(ticked.flatMap((group) => group.pages))].filter(
        (page) => page !== sourceUrl
    )
    const notYetChecked = offered.filter((page) => !pages.includes(page))

    const pattern = check && check.link_pattern
    const hasProblems = Boolean(
        check &&
            (pattern.status === "invalid" ||
                check.invalid_lines.length ||
                check.matching_pages.length)
    )
    const testedLink = (testLink || "").trim()
    // The verdict on the test link, once a check has caught up with what's typed
    const testResult =
        check && check.test_link && check.test_link.link === testedLink
            ? check.test_link
            : null

    const toggle = (group) =>
        onValueChange("link_pattern")(
            combinePatterns(
                groups
                    .filter((other) =>
                        other === group
                            ? !ticked.includes(group)
                            : ticked.includes(other)
                    )
                    .map((other) => other.pattern)
            )
        )

    return (
        <div className="widget">
            <div className="widget-content">
                <h3 className="first-heading">
                    <i className="icon icon-source" />
                    Rendered listing
                </h3>
                <p style={help}>
                    Each time the source is checked, the scraper opens the
                    source URL and any other pages to check, reads the links on
                    them, and collects the ones that are content. The pages
                    themselves aren't collected.
                </p>

                <h3>
                    <i className="icon icon-article" />
                    Set up from the source URL
                </h3>
                <p style={help}>
                    Look at the source URL to see its links, grouped by how
                    their address looks, then tick the ones that are content.
                    Each look uses one Firecrawl credit.
                </p>
                <button
                    type="button"
                    className="btn btn-primary"
                    style={{ marginTop: "8px" }}
                    disabled={!sourceUrl || isDiscovering}
                    onClick={() => onDiscover(sourceUrl)}
                >
                    {isDiscovering
                        ? "Looking at the page…"
                        : "Look at the source URL"}
                </button>
                {!sourceUrl && (
                    <p style={help}>
                        Enter the source URL first: the page where the site
                        lists its latest content.
                    </p>
                )}
                {discoverError && <p style={invalid}>✗ {discoverError}</p>}

                {discover && (
                    <Fragment>
                        <h3>
                            <i className="icon icon-article" />
                            Which links are content?
                        </h3>
                        <p style={help}>
                            {plural(discover.links, "link")} on {discover.url}
                            {discover.other_sites
                                ? `, ${discover.other_sites} of them to other sites, which aren't collected`
                                : ""}
                            . Tick each kind that is content.
                        </p>
                        {groups.map((group) => {
                            const links = linksOf(group)
                            const lists = listedBy(group, groups)
                            return (
                                <div
                                    key={group.shape}
                                    style={{ marginTop: "8px" }}
                                >
                                    <label
                                        style={{
                                            fontWeight: "normal",
                                            marginBottom: "0px",
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={ticked.includes(group)}
                                            onChange={() => toggle(group)}
                                        />{" "}
                                        <Code>{group.shape}</Code>:{" "}
                                        {plural(group.count, "link")}
                                    </label>
                                    {lists && (
                                        <p
                                            className="alert alert-warning"
                                            style={{
                                                ...caution,
                                                marginLeft: "20px",
                                            }}
                                        >
                                            ⚠{" "}
                                            <strong>
                                                Probably not content.
                                            </strong>{" "}
                                            These pages list{" "}
                                            <Code>{lists.shape}</Code>. Tick{" "}
                                            <Code>{lists.shape}</Code> instead,
                                            and these pages are offered under
                                            "Check these pages too?".
                                        </p>
                                    )}
                                    {/* Outside the label, so opening the rest doesn't tick the group */}
                                    <ul style={linkList}>
                                        <li>
                                            <LinkOut href={links[0]} />
                                        </li>
                                    </ul>
                                    {links.length > 1 && (
                                        <details
                                            style={{
                                                ...help,
                                                marginLeft: "20px",
                                            }}
                                        >
                                            <summary
                                                style={{ cursor: "pointer" }}
                                            >
                                                Show {links.length - 1} more
                                            </summary>
                                            <ul
                                                style={{
                                                    ...linkList,
                                                    marginLeft: "0px",
                                                }}
                                            >
                                                {links.slice(1).map((link) => (
                                                    <li key={link}>
                                                        <LinkOut href={link} />
                                                    </li>
                                                ))}
                                                {group.count > links.length && (
                                                    <li>
                                                        … and{" "}
                                                        {group.count -
                                                            links.length}{" "}
                                                        more not listed
                                                    </li>
                                                )}
                                            </ul>
                                        </details>
                                    )}
                                </div>
                            )
                        })}
                        {offered.length > 0 && (
                            <Fragment>
                                <h3>
                                    <i className="icon icon-article" />
                                    Check these pages too?
                                </h3>
                                <p style={help}>
                                    These {plural(offered.length, "page")} are
                                    one level up from the content you ticked, so
                                    they probably list more of it.
                                </p>
                                <ul style={{ ...help, wordBreak: "break-all" }}>
                                    {offered
                                        .slice(0, MAX_PAGES_SHOWN)
                                        .map((page) => (
                                            <li key={page}>
                                                <LinkOut href={page} />
                                                {pages.includes(page)
                                                    ? " ✓"
                                                    : ""}
                                            </li>
                                        ))}
                                </ul>
                                {offered.length > MAX_PAGES_SHOWN && (
                                    <p style={help}>
                                        … and {offered.length - MAX_PAGES_SHOWN}{" "}
                                        more
                                    </p>
                                )}
                                {notYetChecked.length ? (
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        style={{ marginTop: "8px" }}
                                        onClick={() =>
                                            onValueChange("listing_urls")([
                                                ...pages.filter(Boolean),
                                                ...notYetChecked,
                                            ])
                                        }
                                    >
                                        Check{" "}
                                        {notYetChecked.length === offered.length
                                            ? "these"
                                            : `the other ${notYetChecked.length}`}{" "}
                                        {notYetChecked.length === 1
                                            ? "page"
                                            : "pages"}{" "}
                                        too
                                    </button>
                                ) : (
                                    <p style={valid}>✓ They're all checked</p>
                                )}
                            </Fragment>
                        )}
                    </Fragment>
                )}

                <h3>
                    <i className="icon icon-article" />
                    What will be collected
                </h3>
                {checkFailed && (
                    <p style={help}>
                        These settings couldn't be checked just now. They're
                        checked again when you save.
                    </p>
                )}
                {isChecking && <p style={help}>Checking…</p>}
                {check && (
                    <p style={help}>
                        The scraper checks{" "}
                        {plural(check.pages.length + 1, "page")}: the source URL
                        {check.pages.length
                            ? ` and ${check.pages.length} more`
                            : " only"}
                        .
                    </p>
                )}
                {pattern && pattern.status === "valid" && (
                    // Not marked ✓ while listing pages are ticked as content: the warning below says why
                    <p style={misTicked.length ? help : valid}>
                        {misTicked.length ? "" : "✓ "}It collects links that
                        look like{" "}
                        {ticked.length ? (
                            ticked.map((group, index) => (
                                <Fragment key={group.shape}>
                                    {index ? " or " : ""}
                                    <Code>{group.shape}</Code>
                                </Fragment>
                            ))
                        ) : (
                            <Code>{linkPattern}</Code>
                        )}
                    </p>
                )}
                {misTicked.map(({ group, lists }) => (
                    <p
                        key={`listing-${group.shape}`}
                        className="alert alert-warning"
                        style={caution}
                    >
                        ⚠ <Code>{group.shape}</Code> looks like the pages that
                        list <Code>{lists.shape}</Code>, so ticking it collects
                        those pages instead of what they list. Untick it, tick{" "}
                        <Code>{lists.shape}</Code>, and add these pages under
                        "Check these pages too?".
                    </p>
                ))}
                {pattern && pattern.status === "missing" && (
                    <p style={invalid}>
                        ✗ Nothing to collect yet: look at the source URL and
                        tick the links that are content
                    </p>
                )}
                {pattern && pattern.status === "invalid" && (
                    <p style={invalid}>
                        ✗ The link pattern isn't valid: {pattern.error}
                    </p>
                )}
                {check &&
                    check.matching_pages.map((page) => (
                        <p key={`matching-${page}`} style={invalid}>
                            ✗ {page} is one of the pages to check but also looks
                            like content, so it would be collected. Untick the
                            group it's in.
                        </p>
                    ))}

                <details open={hasProblems || undefined}>
                    <summary style={{ marginTop: "15px", cursor: "pointer" }}>
                        Advanced: edit the pages and link pattern yourself
                    </summary>
                    <h3>
                        <i className="icon icon-article" />
                        Other pages to check
                    </h3>
                    <textarea
                        name="listing_urls"
                        rows={6}
                        value={(listingUrls || []).join("\n")}
                        className={`form-control${
                            check && check.invalid_lines.length
                                ? " is-invalid"
                                : ""
                        }`}
                        onChange={(event) =>
                            onValueChange("listing_urls")(
                                event.target.value.split("\n")
                            )
                        }
                    />
                    <p style={help}>
                        One per line. The source URL is always checked, so it
                        needn't be listed.
                    </p>
                    {check &&
                        check.invalid_lines.map(({ line, url: lineUrl }) => (
                            <p key={`invalid-${line}`} style={invalid}>
                                ✗ Line {line} isn't a full web address:{" "}
                                {lineUrl}. Start it with https://
                            </p>
                        ))}
                    {check &&
                        check.repeated_lines.map(({ line, first_line }) => (
                            <p key={`repeated-${line}`} style={help}>
                                {first_line
                                    ? `Line ${line} repeats line ${first_line}, so it's only checked once`
                                    : `Line ${line} is the source URL, which is already checked`}
                            </p>
                        ))}
                    <h3>
                        <i className="icon icon-article" />
                        Link pattern
                    </h3>
                    <input
                        type="text"
                        name="link_pattern"
                        value={linkPattern || ""}
                        className={`form-control${
                            pattern && pattern.status === "invalid"
                                ? " is-invalid"
                                : ""
                        }`}
                        onChange={onEventChange}
                    />
                    <p style={help}>
                        A regular expression that a link's whole address must
                        match to be collected. Ticking links above writes it for
                        you.
                    </p>
                    <h3>
                        <i className="icon icon-article" />
                        Try a link
                    </h3>
                    <input
                        type="text"
                        value={testLink || ""}
                        placeholder="Paste a link from one of the pages"
                        className="form-control"
                        onChange={(event) =>
                            onTestLinkChange(event.target.value)
                        }
                    />
                    {testedLink && pattern && pattern.status !== "valid" && (
                        <p style={help}>
                            Set what to collect first to try this link
                        </p>
                    )}
                    {testResult &&
                        (testResult.matches ? (
                            <p style={valid}>✓ It would be collected</p>
                        ) : (
                            <p style={invalid}>✗ It would be ignored</p>
                        ))}
                </details>
            </div>
        </div>
    )
}

export default SourceEditRenderedListingDetails
